import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ActivityCalendar } from 'react-activity-calendar';
import type { Activity } from 'react-activity-calendar';
import {
  FaFlag,
  FaCalendarAlt,
  FaUser,
  FaRocket,
  FaCheck,
  FaFire,
  FaCrown,
  FaStar,
  FaBriefcase,
  FaGraduationCap,
  FaPencilAlt,
} from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { getAvatarSrc, isDefaultAvatar } from '../utils/avatar.ts';
import { API_BASE } from '../utils/apiEndpoints';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { problemPath } from '../utils/problemUrls.ts';
import {
  catalogProgressTopics,
  directionIconPath,
  directionLabel,
} from '../config/profileDirections.ts';
import { loadProfileExtras } from '../utils/profileExtras.ts';
import { localizeAchievementDescription, localizeAchievementName } from '../utils/achievements.ts';
import 'react-activity-calendar/tooltips.css';
import './css/Profile.css';

const PROFILE_ACTIVITY_THEME = {
  dark: ['#111111', '#151515', '#1a1a1a', '#222222', '#00fcff'],
};

const PROFILE_ACCENT = '#00fcff';
const XP_PER_LEVEL = 5000;
const SHOWCASE_SLOT_COUNT = 5;

function daysLabel(n: number, lang: string): string {
  if (lang === 'ru') {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'день';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'дня';
    return 'дней';
  }
  return n === 1 ? 'day' : 'days';
}

function utcYmd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildActivityCalendarEntries(
  raw: Array<{ date: string; count: number }> | undefined
): Activity[] {
  const end = new Date();
  const endUtc = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  const startUtc = new Date(endUtc);
  startUtc.setUTCDate(startUtc.getUTCDate() - 364);
  const startStr = utcYmd(startUtc);
  const endStr = utcYmd(endUtc);

  const list = Array.isArray(raw)
    ? [...raw]
        .filter((r) => r.date >= startStr && r.date <= endStr)
        .sort((a, b) => a.date.localeCompare(b.date))
    : [];

  const maxCount = Math.max(0, ...list.map((r) => r.count));
  const leveled: Activity[] = list.map(({ date, count }) => {
    let level = 0;
    if (count > 0) {
      if (maxCount <= 1) level = Math.min(4, count);
      else level = Math.max(1, Math.min(4, Math.ceil((count / maxCount) * 4)));
    }
    return { date, count, level };
  });

  const out: Activity[] = [];
  if (leveled.length === 0 || leveled[0].date > startStr) {
    out.push({ date: startStr, count: 0, level: 0 });
  }
  out.push(...leveled);
  if (leveled.length === 0 || leveled[leveled.length - 1].date < endStr) {
    out.push({ date: endStr, count: 0, level: 0 });
  }
  return out;
}

const ACTIVITY_GRID_WEEKS = 54;
const ACTIVITY_GRID_ROWS = 7;
const ACTIVITY_BLOCK_MARGIN = 2;
const ACTIVITY_TARGET_BLOCK_SIZE = 12;
const ACTIVITY_MIN_BLOCK_SIZE = 10;
const ACTIVITY_MAX_BLOCK_SIZE = 14;

function weekdayLabelsReservePx(fontSize: number): number {
  return Math.ceil(26 + fontSize * 1.2) + 8;
}

function computeActivityBlockSize(
  containerWidthPx: number,
  containerHeightPx: number
): { blockSize: number; fontSize: number } {
  const horizontalPadding = 8;
  const verticalReserve = 54;
  const gutter = weekdayLabelsReservePx(11);
  const innerW = Math.max(100, containerWidthPx - horizontalPadding - gutter);
  const innerH = Math.max(70, containerHeightPx - verticalReserve);

  const bsFromWidth = Math.floor(
    (innerW + ACTIVITY_BLOCK_MARGIN) / ACTIVITY_GRID_WEEKS - ACTIVITY_BLOCK_MARGIN
  );
  const bsFromHeight = Math.floor(
    (innerH + ACTIVITY_BLOCK_MARGIN) / ACTIVITY_GRID_ROWS - ACTIVITY_BLOCK_MARGIN
  );

  let bs = ACTIVITY_TARGET_BLOCK_SIZE;
  if (bsFromHeight < bs) {
    bs = Math.max(ACTIVITY_MIN_BLOCK_SIZE, bsFromHeight);
  }
  if (bsFromWidth >= ACTIVITY_MIN_BLOCK_SIZE) {
    bs = Math.min(ACTIVITY_MAX_BLOCK_SIZE, Math.max(bs, bsFromWidth));
  }

  bs = Math.max(ACTIVITY_MIN_BLOCK_SIZE, Math.min(ACTIVITY_MAX_BLOCK_SIZE, bs));
  const fontSize = Math.max(10, Math.min(12, bs));
  return { blockSize: bs, fontSize };
}

function formatNum(n: number, locale: string): string {
  return n.toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US');
}

function computeXp(stats: { easy: number; medium: number; hard: number; total: number }, achievementPts: number): number {
  return stats.total * 50 + achievementPts * 10;
}

function getLevelInfo(xp: number) {
  const level = Math.max(1, Math.floor(xp / XP_PER_LEVEL) + 1);
  const xpInLevel = xp % XP_PER_LEVEL;
  const progress = (xpInLevel / XP_PER_LEVEL) * 100;
  return { level, xpInLevel, progress, nextLevel: level + 1, xpTarget: XP_PER_LEVEL };
}

function computeStreaks(calendar: Array<{ date: string; count: number }> | undefined) {
  const activeDates = new Set(
    (calendar ?? []).filter((d) => d.count > 0).map((d) => d.date)
  );
  if (activeDates.size === 0) return { best: 0, current: 0 };

  const sorted = [...activeDates].sort();
  let best = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(`${sorted[i - 1]}T12:00:00Z`);
    const cur = new Date(`${sorted[i]}T12:00:00Z`);
    const diff = (cur.getTime() - prev.getTime()) / 86400000;
    if (diff === 1) {
      run++;
      best = Math.max(best, run);
    } else {
      run = 1;
    }
  }

  let current = 0;
  const today = new Date();
  let d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  while (activeDates.has(utcYmd(d))) {
    current++;
    d.setUTCDate(d.getUTCDate() - 1);
  }

  return { best, current };
}

function getLeagueLabel(rank: number | string | null, lang: string): string {
  const r = typeof rank === 'number' ? rank : parseInt(String(rank), 10);
  if (!Number.isFinite(r) || r <= 0) return lang === 'ru' ? 'Стартер' : 'Starter';
  if (r <= 10) return lang === 'ru' ? 'Золото I' : 'Gold I';
  if (r <= 50) return lang === 'ru' ? 'Платина II' : 'Platinum II';
  if (r <= 200) return lang === 'ru' ? 'Серебро I' : 'Silver I';
  if (r <= 500) return lang === 'ru' ? 'Бронза II' : 'Bronze II';
  return lang === 'ru' ? 'Стартер' : 'Starter';
}

function formatJoinDate(joinDate: string, lang: string): string {
  const d = new Date(joinDate);
  if (Number.isNaN(d.getTime())) return joinDate;
  return d.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatSolvedDate(solvedAt: string, lang: string): string {
  const d = new Date(solvedAt);
  if (Number.isNaN(d.getTime())) return solvedAt;
  return d.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function difficultyLabel(difficulty: string, t: (key: string) => string): string {
  switch (difficulty) {
    case 'Easy':
      return t('problemsList.difficultyEasy');
    case 'Medium':
      return t('problemsList.difficultyMedium');
    case 'Hard':
      return t('problemsList.difficultyHard');
    default:
      return difficulty;
  }
}

function topicLabel(topic: string, t: (key: string) => string): string {
  return directionLabel(topic, t);
}

function topicProgressPercent(
  solved: number,
  total: number
): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((solved / total) * 100));
}

interface UserProfile {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email?: string;
  avatar: string;
  joinDate: string;
  phone?: string;
  rank: string | number | null;
  job_title: string[];
  university?: string;
  skills: string[];
  birthday?: string | null;
  solvedStats: { total: number; easy: number; medium: number; hard: number };
  solvedByTopic?: Record<string, number>;
  solvedByTopicDetail?: Record<string, { total: number; easy: number; medium: number; hard: number }>;
  topics?: string[];
  achievements?: Array<{ id: number; name: string; description?: string; points: number; icon?: string | null; earned_at?: string }>;
  achievementPoints?: number;
  achievementsTotal?: number;
  achievementShowcase?: Array<AchievementShowcaseItem | null>;
  totalProblems?: { total: number; easy: number; medium: number; hard: number };
  totalProblemsByTopic?: Record<string, { total: number; easy: number; medium: number; hard: number }>;
  activityCalendar?: Array<{ date: string; count: number }>;
  recentSolvedTasks?: Array<{
    id: number;
    title: string;
    topic: string;
    slug?: string | null;
    difficulty: 'Easy' | 'Medium' | 'Hard' | string;
    solvedAt: string;
    xp: number;
  }>;
}

interface AchievementShowcaseItem {
  id: number;
  name: string;
  description: string | null;
  icon?: string | null;
  key?: string | null;
}

function emptyShowcaseSlots(): Array<AchievementShowcaseItem | null> {
  return Array.from({ length: SHOWCASE_SLOT_COUNT }, () => null);
}

function normalizeShowcaseSlots(raw: Array<AchievementShowcaseItem | null> | undefined): Array<AchievementShowcaseItem | null> {
  const slots = emptyShowcaseSlots();
  if (!Array.isArray(raw)) return slots;
  raw.slice(0, SHOWCASE_SLOT_COUNT).forEach((item, index) => {
    if (item && typeof item.id === 'number') slots[index] = item;
  });
  return slots;
}

function Profile() {
  const navigate = useNavigate();
  const { username: profileUsername } = useParams<{ username?: string }>();
  const { user } = useAuth();
  const { t, currentLanguage } = useTranslation();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [achievementShowcase, setAchievementShowcase] = useState<Array<AchievementShowcaseItem | null>>(emptyShowcaseSlots());
  const [earnedAchievements, setEarnedAchievements] = useState<AchievementShowcaseItem[]>([]);
  const [showcaseEditSlot, setShowcaseEditSlot] = useState<number | null>(null);
  const [showcaseSaving, setShowcaseSaving] = useState(false);
  const [showcaseError, setShowcaseError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState<'spam' | 'abuse' | 'hate' | 'impersonation' | 'inappropriate_content' | 'other'>('spam');
  const [reportMessage, setReportMessage] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportToast, setReportToast] = useState<string | null>(null);

  const activitySectionRef = useRef<HTMLDivElement | null>(null);
  const [activityCalendarSize, setActivityCalendarSize] = useState({ blockSize: 12, fontSize: 11 });

  useEffect(() => {
    const isOwnById = profileUsername && user?.id != null && /^\d+$/.test(profileUsername) && String(user.id) === profileUsername;
    const isOwnByUsername = profileUsername && user?.username && user.username.toLowerCase() === profileUsername.toLowerCase();
    if (isOwnById || isOwnByUsername) {
      navigate('/profile', { replace: true });
      return;
    }

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const langQ = `?lang=${encodeURIComponent(currentLanguage)}`;
        if (profileUsername) {
          const r = await fetch(
            `${API_BASE}/api/profile/user/${encodeURIComponent(profileUsername)}${langQ}`,
            { credentials: 'include' }
          );
          if (r.status === 404) {
            setError('PROFILE_NOT_FOUND');
            setUserProfile(null);
            return;
          }
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          setUserProfile(await r.json());
        } else {
          const r = await fetch(`${API_BASE}/api/profile/profile${langQ}`, {
            method: 'GET',
            credentials: 'include',
          });
          if (!r.ok) {
            if (r.status === 401) {
              navigate('/login');
              return;
            }
            throw new Error(`HTTP error! status: ${r.status}`);
          }
          setUserProfile(await r.json());
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '__unknown__');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [navigate, profileUsername, user?.id, user?.username, currentLanguage]);

  useEffect(() => {
    if (!userProfile) {
      setAchievementShowcase(emptyShowcaseSlots());
      return;
    }
    setAchievementShowcase(normalizeShowcaseSlots(userProfile.achievementShowcase));
  }, [userProfile?.id, userProfile?.achievementShowcase]);

  useEffect(() => {
    const isOwnProfile = !profileUsername;
    if (!isOwnProfile || !userProfile?.id) {
      setEarnedAchievements([]);
      return;
    }
    const run = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/achievements/me`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        const list = (data.achievements ?? []).map((a: AchievementShowcaseItem & { earned_at?: string; criteria?: { key?: string } }) => ({
          id: a.id,
          name: a.name,
          description: a.description ?? null,
          icon: a.icon,
          key: a.key || a.criteria?.key || null,
        }));
        setEarnedAchievements(list);
      } catch {
        setEarnedAchievements([]);
      }
    };
    run();
  }, [profileUsername, userProfile?.id]);

  const calendarLocale = currentLanguage === 'ru' ? 'ru-RU' : 'en-US';

  const activityEntries = useMemo(
    () => buildActivityCalendarEntries(userProfile?.activityCalendar),
    [userProfile?.activityCalendar]
  );

  const calendarLabels = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleDateString(calendarLocale, { month: 'short' }).toUpperCase()
    );
    const monday = new Date(2024, 0, 8);
    const weekdays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.toLocaleDateString(calendarLocale, { weekday: 'short' }).slice(0, 2).toUpperCase();
    });
    return {
      months,
      weekdays,
      legend: { less: t('profile.activityLess'), more: t('profile.activityMore') },
    };
  }, [calendarLocale, t]);

  const activityTooltipText = useCallback(
    (a: Activity) => {
      const formatted = new Date(`${a.date}T12:00:00Z`).toLocaleDateString(calendarLocale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      return t('profile.activityTooltip').replace('{{count}}', String(a.count)).replace('{{date}}', formatted);
    },
    [calendarLocale, t]
  );

  useLayoutEffect(() => {
    if (loading || error || !userProfile) return;
    const el = activitySectionRef.current;
    if (!el) return;

    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width < 16 || height < 16) return;
      const { blockSize, fontSize } = computeActivityBlockSize(width, height);
      setActivityCalendarSize((prev) =>
        prev.blockSize === blockSize && prev.fontSize === fontSize ? prev : { blockSize, fontSize }
      );
    };

    update();
    const ro = new ResizeObserver(() => requestAnimationFrame(update));
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, error, userProfile, activityEntries.length, calendarLocale]);

  const isOwnProfile = !profileUsername;

  const profileExtras = useMemo(
    () => (userProfile ? loadProfileExtras(userProfile.id) : null),
    [userProfile?.id]
  );

  const derived = useMemo(() => {
    if (!userProfile) return null;
    const achievementPts =
      userProfile.achievementPoints ??
      (userProfile.achievements ?? []).reduce((s, a) => s + (a.points ?? 0), 0);
    const xp = computeXp(userProfile.solvedStats, achievementPts);
    const levelInfo = getLevelInfo(xp);
    const streaks = computeStreaks(userProfile.activityCalendar);
    const league = getLeagueLabel(userProfile.rank, currentLanguage);

    const directions = catalogProgressTopics(userProfile.topics).map((topic) => {
      const solved = userProfile.solvedByTopic?.[topic] ?? 0;
      const total = userProfile.totalProblemsByTopic?.[topic]?.total ?? 0;
      return {
        topic,
        iconPath: directionIconPath(topic),
        label: directionLabel(topic, t),
        solved,
        total,
        percent: topicProgressPercent(solved, total),
      };
    });

    return { achievementPts, xp, levelInfo, streaks, league, directions };
  }, [userProfile, currentLanguage, t]);

  const openReportModal = () => {
    setReportError(null);
    setReportReason('spam');
    setReportMessage('');
    setReportOpen(true);
  };

  const closeReportModal = () => {
    if (reportSubmitting) return;
    setReportOpen(false);
    setReportError(null);
  };

  useCloseOnEscape(reportOpen, closeReportModal);

  const showcaseModalOpen = showcaseEditSlot !== null;

  const closeShowcaseModal = useCallback(() => {
    if (showcaseSaving) return;
    setShowcaseEditSlot(null);
    setShowcaseError(null);
  }, [showcaseSaving]);

  useCloseOnEscape(showcaseModalOpen, closeShowcaseModal);

  const saveShowcaseSlot = async (slotIndex: number, achievementId: number | null) => {
    setShowcaseSaving(true);
    setShowcaseError(null);
    const nextSlots = achievementShowcase.map((item, index) => {
      if (index === slotIndex) {
        if (achievementId == null) return null;
        return earnedAchievements.find((a) => a.id === achievementId) ?? item;
      }
      return item;
    });
    const payload = nextSlots.map((item) => (item ? item.id : null));
    try {
      const r = await fetch(`${API_BASE}/api/profile/achievement-showcase`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ slots: payload }),
      });
      if (r.status === 401) {
        navigate('/login');
        return;
      }
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      const resolved = normalizeShowcaseSlots(data.achievementShowcase);
      setAchievementShowcase(resolved);
      setUserProfile((prev) => (prev ? { ...prev, achievementShowcase: resolved } : prev));
      setShowcaseEditSlot(null);
    } catch (e) {
      setShowcaseError(e instanceof Error ? e.message : t('profile.achievementShowcaseSaveFailed'));
    } finally {
      setShowcaseSaving(false);
    }
  };

  const submitReport = async () => {
    if (!userProfile || profileUsername == null) return;
    if (user?.id != null && String(user.id) === String(userProfile.id)) return;
    setReportSubmitting(true);
    setReportError(null);
    try {
      const r = await fetch(`${API_BASE}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          reportedUserId: userProfile.id,
          reason: reportReason,
          message: reportMessage,
        }),
      });
      if (r.status === 401) {
        navigate('/login');
        return;
      }
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || `HTTP ${r.status}`);
      setReportOpen(false);
      setReportToast(t('profile.reportSent'));
      window.setTimeout(() => setReportToast(null), 3500);
    } catch (e) {
      setReportError(e instanceof Error ? e.message : t('profile.reportFailed'));
    } finally {
      setReportSubmitting(false);
    }
  };

  if (loading) return <div className="loading">{t('profile.loading')}</div>;
  if (error) {
    const msg = error === 'PROFILE_NOT_FOUND' ? t('profile.notFound') : `${t('profile.errorPrefix')} ${error === '__unknown__' ? t('profile.unknownError') : error}`;
    return <div className="error">{msg}</div>;
  }
  if (!userProfile || !derived) return <div className="error">{t('profile.noProfileData')}</div>;

  const canReportProfile =
    !!profileUsername &&
    user?.id != null &&
    String(user?.id) !== String(userProfile.id);

  const jobTitles = Array.isArray(userProfile.job_title) ? userProfile.job_title.filter(Boolean) : [];
  const displayName = [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ').trim() || userProfile.username;
  const jobDisplay = jobTitles.length > 0 ? jobTitles.join(', ') : null;

  const educationParts = [
    userProfile.university?.trim() || '',
    profileExtras?.specialty?.trim() || '',
    profileExtras?.gradYear?.trim() || '',
  ].filter(Boolean);
  const universityDisplay = educationParts.length > 0 ? educationParts.join(', ') : null;

  const joinDateFormatted = formatJoinDate(userProfile.joinDate, currentLanguage);
  const recentSolvedTasks = userProfile.recentSolvedTasks ?? [];
  const achievementsLink = isOwnProfile ? '/achievements' : `/achievements/${userProfile.id}`;
  const xpProgressLabel = t('profile.levelXpProgress')
    .replace('{{current}}', formatNum(derived.levelInfo.xpInLevel, currentLanguage))
    .replace('{{total}}', formatNum(derived.levelInfo.xpTarget, currentLanguage))
    .replace('{{next}}', String(derived.levelInfo.nextLevel));

  return (
    <div className="profile">
      {reportToast && (
        <div className="profile-toast" role="status">
          {reportToast}
        </div>
      )}

      <div className="profile-container">
        <section className="profile-card profile-hero">
          <div className="profile-hero__identity">
            <div className="profile-hero__avatar-wrap">
              {isDefaultAvatar(userProfile.avatar) ? (
                <div className="profile-hero__avatar profile-hero__avatar--fallback" role="img" aria-label={displayName}>
                  <FaUser />
                </div>
              ) : (
                <img
                  src={getAvatarSrc(userProfile.avatar)}
                  alt={displayName}
                  className="profile-hero__avatar"
                />
              )}
            </div>

            <div className="profile-hero__identity-text">
              <div className="profile-hero__name-row">
                <h1 className="profile-hero__name">{displayName}</h1>
                {canReportProfile && (
                  <button
                    type="button"
                    className="profile-report-btn"
                    onClick={openReportModal}
                    aria-label={t('profile.reportButtonAria')}
                    title={t('profile.reportButtonTitle')}
                  >
                    <FaFlag />
                  </button>
                )}
              </div>

              <div className="profile-hero__handle-row">
                <p className="profile-hero__handle">@{userProfile.username}</p>
              </div>

              <ul className="profile-hero__meta">
                {jobDisplay && (
                  <li>
                    <FaBriefcase aria-hidden />
                    <span>{jobDisplay}</span>
                  </li>
                )}
                {universityDisplay && (
                  <li>
                    <FaGraduationCap aria-hidden />
                    <span>{universityDisplay}</span>
                  </li>
                )}
                <li>
                  <FaCalendarAlt aria-hidden />
                  <span>{t('profile.onPlatformSince')} {joinDateFormatted}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="profile-hero__stats-grid">
            <div className="profile-hero-stat-card">
              <div className="profile-hero-stat__head">
                <FaRocket className="profile-hero-stat__icon" aria-hidden />
                <span>{t('profile.levelLabel')} {derived.levelInfo.level}</span>
              </div>
              <div className="profile-hero-stat__progress">
                <div
                  className="profile-hero-stat__progress-fill"
                  style={{ width: `${derived.levelInfo.progress}%` }}
                />
              </div>
              <span className="profile-hero-stat__sub">{xpProgressLabel}</span>
            </div>

            <div className="profile-hero-stat-card">
              <div className="profile-hero-stat__head">
                <FaCheck className="profile-hero-stat__icon" aria-hidden />
                <span>{t('profile.tasksSolved')}</span>
              </div>
              <span className="profile-hero-stat__value">
                {formatNum(userProfile.solvedStats.total, currentLanguage)}
              </span>
            </div>

            <div className="profile-hero-stat-card">
              <div className="profile-hero-stat__head">
                <FaFire className="profile-hero-stat__icon" aria-hidden />
                <span>{t('profile.activityStreak')}</span>
              </div>
              <span className="profile-hero-stat__value">
                {derived.streaks.current} {daysLabel(derived.streaks.current, currentLanguage)}
              </span>
            </div>

            <div className="profile-hero-stat-card profile-hero-stat-card--rating">
              <div className="profile-hero-stat__head">
                <FaCrown className="profile-hero-stat__icon profile-hero-stat__icon--gold" aria-hidden />
                <span>{t('profile.rating')}</span>
              </div>
              <span className="profile-hero-stat__value profile-hero-stat__value--gold">
                {derived.league}
              </span>
            </div>
          </div>
        </section>

        <div className="profile-content-grid">
          <div className="profile-card profile-panel">
            <h2 className="profile-panel__title">{t('profile.yourActivity')}</h2>
            <div className="profile-activity-section" ref={activitySectionRef}>
              <ActivityCalendar
                data={activityEntries}
                theme={PROFILE_ACTIVITY_THEME}
                colorScheme="dark"
                blockSize={activityCalendarSize.blockSize}
                blockMargin={ACTIVITY_BLOCK_MARGIN}
                blockRadius={2}
                fontSize={activityCalendarSize.fontSize}
                weekStart={1}
                showWeekdayLabels
                showTotalCount={false}
                labels={calendarLabels}
                className="profile-activity-calendar-inner"
                tooltips={{ activity: { text: activityTooltipText } }}
              />
            </div>
          </div>

          <div className="profile-card profile-panel">
            <h2 className="profile-panel__title">{t('profile.progressByDirections')}</h2>
            <ul className="profile-directions-list">
              {derived.directions.map((dir) => (
                <li key={dir.topic} className="profile-direction-item">
                  {dir.iconPath ? (
                    <img
                      src={dir.iconPath}
                      alt=""
                      className="profile-direction-item__icon"
                    />
                  ) : (
                    <span className="profile-direction-item__icon" aria-hidden />
                  )}
                  <span className="profile-direction-item__label">{dir.label}</span>
                  <div className="profile-direction-item__bar-wrap">
                    <div
                      className="profile-direction-item__bar"
                      style={{ width: `${dir.percent}%` }}
                    />
                  </div>
                  <span className="profile-direction-item__pct">{dir.percent}%</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="profile-card profile-panel">
            <div className="profile-panel__header">
              <h2 className="profile-panel__title">{t('profile.achievements')}</h2>
              <Link to={achievementsLink} className="profile-panel__link">
                {t('profile.allAchievementsArrow')}
              </Link>
            </div>
            <div className="profile-achievements-row">
              {achievementShowcase.map((slot, slotIndex) => {
                const iconPath = slot
                  ? slot.icon
                    ? `/achievement-icons/${slot.icon}`
                    : `/achievement-icons/${slot.id}.svg`
                  : null;
                return (
                  <div
                    key={`showcase-slot-${slotIndex}`}
                    className={`profile-achievement-item${slot ? ' profile-achievement-item--earned' : ''}`}
                  >
                    <div
                      className={`profile-achievement-hex-wrap${isOwnProfile ? ' profile-achievement-hex-wrap--editable' : ''}`}
                    >
                      <div className={`profile-achievement-hex${slot ? '' : ' profile-achievement-hex--empty'}`}>
                        {slot && iconPath ? (
                          <>
                            <img
                              src={iconPath}
                              alt=""
                              className="profile-achievement-hex-img"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                (e.target as HTMLImageElement).nextElementSibling?.classList.add('profile-achievement-hex-fallback-visible');
                              }}
                            />
                            <FaStar className="profile-achievement-hex-star profile-achievement-hex-fallback" aria-hidden />
                          </>
                        ) : null}
                      </div>
                      {isOwnProfile && (
                        <button
                          type="button"
                          className="profile-achievement-hex-edit"
                          onClick={() => {
                            setShowcaseError(null);
                            setShowcaseEditSlot(slotIndex);
                          }}
                          aria-label={t('profile.achievementShowcaseEditAria')}
                          title={t('profile.achievementShowcaseEditAria')}
                        >
                          <FaPencilAlt aria-hidden />
                        </button>
                      )}
                    </div>
                    {slot && (
                      <>
                        <span className="profile-achievement-item__name">{localizeAchievementName(t, slot)}</span>
                        {localizeAchievementDescription(t, slot) && (
                          <span className="profile-achievement-item__desc">{localizeAchievementDescription(t, slot)}</span>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="profile-card profile-panel">
            <h2 className="profile-panel__title">{t('profile.recentTasks')}</h2>
            {recentSolvedTasks.length > 0 ? (
              <div className="profile-recent-table-wrap">
                <table className="profile-recent-table">
                  <thead>
                    <tr>
                      <th>{t('profile.recentColTitle')}</th>
                      <th>{t('profile.recentColTopic')}</th>
                      <th>{t('profile.recentColDifficulty')}</th>
                      <th>{t('profile.recentColDate')}</th>
                      <th>{t('profile.recentColXp')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentSolvedTasks.map((task) => (
                      <tr key={`${task.id}-${task.solvedAt}`}>
                        <td className="profile-recent-table__title">
                          <Link to={problemPath(task)}>{task.title}</Link>
                        </td>
                        <td>{topicLabel(task.topic, t)}</td>
                        <td>
                          <span className={`profile-recent-difficulty profile-recent-difficulty--${task.difficulty.toLowerCase()}`}>
                            {difficultyLabel(task.difficulty, t)}
                          </span>
                        </td>
                        <td className="profile-recent-table__date">
                          {formatSolvedDate(task.solvedAt, currentLanguage)}
                        </td>
                        <td className="profile-recent-table__xp">+{task.xp}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="profile-recent-empty">{t('profile.noRecentTasks')}</p>
            )}
          </div>
        </div>
      </div>

      {showcaseModalOpen && (
        <div className="profile-report-modal-overlay" onClick={closeShowcaseModal} role="presentation">
          <div className="profile-showcase-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="profile-report-modal-header">
              <h2 className="profile-report-modal-title">{t('profile.achievementShowcaseModalTitle')}</h2>
              <button
                type="button"
                className="profile-report-modal-close"
                onClick={closeShowcaseModal}
                aria-label={t('profile.reportCloseAria')}
                disabled={showcaseSaving}
              >
                ×
              </button>
            </div>
            {showcaseError && <div className="profile-report-error">{showcaseError}</div>}
            {earnedAchievements.length === 0 ? (
              <p className="profile-showcase-modal-empty">{t('profile.achievementShowcaseNoEarned')}</p>
            ) : (
              <ul className="profile-showcase-modal-list">
                {earnedAchievements.map((a) => {
                  const iconPath = a.icon ? `/achievement-icons/${a.icon}` : `/achievement-icons/${a.id}.svg`;
                  const isSelected = showcaseEditSlot != null && achievementShowcase[showcaseEditSlot]?.id === a.id;
                  return (
                    <li key={a.id}>
                      <button
                        type="button"
                        className={`profile-showcase-modal-option${isSelected ? ' profile-showcase-modal-option--selected' : ''}`}
                        disabled={showcaseSaving}
                        onClick={() => showcaseEditSlot != null && saveShowcaseSlot(showcaseEditSlot, a.id)}
                      >
                        <span className="profile-showcase-modal-option-icon">
                          <img
                            src={iconPath}
                            alt=""
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </span>
                        <span className="profile-showcase-modal-option-text">
                          <span className="profile-showcase-modal-option-name">{localizeAchievementName(t, a)}</span>
                          {localizeAchievementDescription(t, a) && (
                            <span className="profile-showcase-modal-option-desc">{localizeAchievementDescription(t, a)}</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="profile-report-actions">
              <button
                type="button"
                className="profile-report-action profile-report-cancel"
                onClick={closeShowcaseModal}
                disabled={showcaseSaving}
              >
                {t('profile.reportCancel')}
              </button>
              {achievementShowcase[showcaseEditSlot ?? -1] && (
                <button
                  type="button"
                  className="profile-report-action profile-report-submit"
                  disabled={showcaseSaving}
                  onClick={() => showcaseEditSlot != null && saveShowcaseSlot(showcaseEditSlot, null)}
                >
                  {t('profile.achievementShowcaseClear')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {reportOpen && (
        <div className="profile-report-modal-overlay" onClick={closeReportModal} role="presentation">
          <div className="profile-report-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="profile-report-modal-header">
              <h2 className="profile-report-modal-title">{t('profile.reportModalTitle')}</h2>
              <button
                type="button"
                className="profile-report-modal-close"
                onClick={closeReportModal}
                aria-label={t('profile.reportCloseAria')}
                disabled={reportSubmitting}
              >
                ×
              </button>
            </div>
            {reportError && <div className="profile-report-error">{reportError}</div>}
            <div className="profile-report-field">
              <label htmlFor="profile-report-reason" className="profile-report-label">{t('profile.reportReasonLabel')}</label>
              <select
                id="profile-report-reason"
                className="profile-report-select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value as typeof reportReason)}
                disabled={reportSubmitting}
              >
                <option value="spam">{t('profile.reportReasonSpam')}</option>
                <option value="abuse">{t('profile.reportReasonAbuse')}</option>
                <option value="hate">{t('profile.reportReasonHate')}</option>
                <option value="impersonation">{t('profile.reportReasonImpersonation')}</option>
                <option value="inappropriate_content">{t('profile.reportReasonInappropriate')}</option>
                <option value="other">{t('profile.reportReasonOther')}</option>
              </select>
            </div>
            <div className="profile-report-field">
              <label htmlFor="profile-report-message" className="profile-report-label">{t('profile.reportMessageLabel')}</label>
              <textarea
                id="profile-report-message"
                className="profile-report-textarea"
                value={reportMessage}
                onChange={(e) => setReportMessage(e.target.value)}
                placeholder={t('profile.reportMessagePlaceholder')}
                rows={5}
                maxLength={2000}
                disabled={reportSubmitting}
              />
            </div>
            <div className="profile-report-actions">
              <button type="button" className="profile-report-action profile-report-cancel" onClick={closeReportModal} disabled={reportSubmitting}>
                {t('profile.reportCancel')}
              </button>
              <button type="button" className="profile-report-action profile-report-submit" onClick={submitReport} disabled={reportSubmitting}>
                {reportSubmitting ? t('profile.reportSending') : t('profile.reportSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
